import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "../../hooks/useAuth0";
import {
  Container, Typography, Box, Paper, Button,
  CircularProgress, Alert, Grid, Chip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchIcon from "@mui/icons-material/Search";
import LockIcon from "@mui/icons-material/Lock";
import api from "../../services/api";

// ── Reuse existing TrialCard from ClinicalTrialsSearch ────────────────────────
// Import directly from the existing component file
import { TrialCard } from "./ClinicalTrialsSearch";

const EXAMPLE_PROMPTS = [
  "I have ATTR heart disease, I'm 68 and on tafamidis but it's not working well",
  "I was just diagnosed with AL amyloidosis and my doctor mentioned clinical trials",
  "I have wild-type ATTR cardiomyopathy and looking for new treatment options",
  "I have hereditary ATTR with the V122I mutation, I'm African American and 65",
  "I have AA amyloidosis from rheumatoid arthritis, my kidneys are affected",
];

const GUEST_LIMIT = 1;

export default function NLPTrialSearch() {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setHasSearched(true);

    try {
      const response = await api.post("/v1/trials/nlp-search", {
        query: query.trim(),
        authenticated: isAuthenticated,
      });
      setResult(response.data);
    } catch (err) {
      setError("Unable to search right now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  // Trials to show — limit for guests
  const trialsToShow = result?.trials || [];
  const visibleTrials = isAuthenticated
    ? trialsToShow
    : trialsToShow.slice(0, GUEST_LIMIT);
  const hiddenCount = trialsToShow.length - visibleTrials.length;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>

      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mb: 2,
          bgcolor: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)",
          borderRadius: 10, px: 2, py: 0.5 }}>
          <AutoAwesomeIcon sx={{ fontSize: 14, color: "#6366F1" }} />
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#6366F1",
            letterSpacing: "0.08em", textTransform: "uppercase" }}>
            AI-Powered Trial Matching
          </Typography>
        </Box>
        <Typography variant="h3" component="h1" gutterBottom sx={{
          fontWeight: "bold",
          background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
          backgroundClip: "text", WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}>
          Find My Trial
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Describe your situation in plain English — no medical jargon required
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Our AI understands what you mean and matches you to the right trials
        </Typography>
      </Box>

      {/* Search box */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, maxWidth: 800, mx: "auto" }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary" }}>
          Describe your situation
        </Typography>

        <textarea
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Example: I have ATTR heart disease, I'm 68 years old and on tafamidis but my symptoms are getting worse..."
          rows={4}
          style={{
            width: "100%", padding: "14px 16px",
            border: "2px solid #E5E7EB", borderRadius: 10,
            fontSize: 15, lineHeight: 1.6, color: "#1F2937",
            background: "#FAFAFA", resize: "vertical",
            fontFamily: "inherit", boxSizing: "border-box",
            transition: "border-color 0.2s", outline: "none",
          }}
          onFocus={e => e.target.style.borderColor = "#6366F1"}
          onBlur={e => e.target.style.borderColor = "#E5E7EB"}
        />

        {/* Example prompts */}
        <Box sx={{ mt: 1.5, mb: 2.5 }}>
          <Typography variant="caption" sx={{ color: "text.disabled",
            fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, display: "block" }}>
            Try an example:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {EXAMPLE_PROMPTS.map((prompt, i) => (
              <Chip
                key={i}
                label={prompt.length > 55 ? prompt.slice(0, 52) + "..." : prompt}
                onClick={() => setQuery(prompt)}
                size="small"
                variant="outlined"
                sx={{
                  cursor: "pointer", fontSize: 11,
                  borderColor: "rgba(99,102,241,0.3)",
                  color: "#6366F1",
                  "&:hover": { bgcolor: "rgba(99,102,241,0.08)", borderColor: "#6366F1" },
                }}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {isAuthenticated
              ? "✅ Signed in — showing full results"
              : "🔒 Sign in to see all matched trials"}
          </Typography>
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={!query.trim() || loading}
            startIcon={loading
              ? <CircularProgress size={16} color="inherit" />
              : <AutoAwesomeIcon />}
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
              px: 4, py: 1.25, fontWeight: 700, flexShrink: 0,
              "&:hover": { background: "linear-gradient(135deg, #5568d3 0%, #653a8b 100%)" },
              "&:disabled": { opacity: 0.5 },
            }}
          >
            {loading ? "Matching..." : "Find My Trial ✨"}
          </Button>
        </Box>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, maxWidth: 800, mx: "auto" }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {result && !loading && (
        <Box>
          {/* AI Summary */}
          {result.summary && (
            <Paper elevation={2} sx={{
              p: 3, mb: 4, maxWidth: 800, mx: "auto",
              bgcolor: "rgba(102,126,234,0.05)",
              border: "1px solid rgba(102,126,234,0.2)",
            }}>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <AutoAwesomeIcon sx={{ color: "#1A5EA8", mt: 0.25, flexShrink: 0 }} />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#1A5EA8",
                    textTransform: "uppercase", letterSpacing: "0.06em", display: "block", mb: 0.5 }}>
                    AI Summary
                  </Typography>
                  <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.7 }}>
                    {result.summary}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Results count */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {trialsToShow.length} Trial{trialsToShow.length !== 1 ? "s" : ""} Found
            </Typography>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => navigate("/amyloid/clinicaltrialsearch")}
              size="small"
              sx={{ borderColor: "#1A5EA8", color: "#1A5EA8" }}
            >
              Classic Search
            </Button>
          </Box>

          {/* Trial cards — reusing existing TrialCard component */}
          <Grid container spacing={3}>
            {visibleTrials.map((trial, index) => (
              <Grid item xs={12} key={`nlp-${trial.nctId || trial.id}-${index}`}>
                {/* Inject AI reason above card if available */}
                {result.reasons?.[index] && (
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    mb: 1, px: 2, py: 1,
                    bgcolor: "rgba(102,126,234,0.06)",
                    border: "1px solid rgba(102,126,234,0.15)",
                    borderRadius: 2,
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 14, color: "#1A5EA8" }} />
                    <Typography variant="body2" sx={{ color: "#1A5EA8", fontWeight: 500 }}>
                      {result.reasons[index]}
                    </Typography>
                  </Box>
                )}
                <TrialCard trial={{ ...trial, source: trial.source || "internal" }} />
              </Grid>
            ))}
          </Grid>

          {/* Guest upgrade prompt */}
          {!isAuthenticated && hiddenCount > 0 && (
            <Paper elevation={3} sx={{
              mt: 3, p: 4, textAlign: "center",
              background: "linear-gradient(135deg, rgba(102,126,234,0.08), rgba(118,75,162,0.08))",
              border: "1px solid rgba(102,126,234,0.25)",
            }}>
              <LockIcon sx={{ fontSize: 40, color: "#1A5EA8", mb: 1 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {hiddenCount} more trial{hiddenCount !== 1 ? "s" : ""} found for you
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Sign in for free to see all matched trials, save your results, and get personalized matching.
              </Typography>
              <Button
                variant="contained"
                onClick={() => loginWithRedirect({ appState: { returnTo: "/trials/nlp-search" } })}
                sx={{
                  background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                  px: 4, py: 1.25, fontWeight: 700,
                }}
              >
                Sign In Free →
              </Button>
            </Paper>
          )}
        </Box>
      )}

      {/* Empty state */}
      {!hasSearched && !loading && (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Not sure what to type?{" "}
            <span
              style={{ color: "#1A5EA8", cursor: "pointer", fontWeight: 600 }}
              onClick={() => navigate("/education/types")}
            >
              Learn about amyloidosis types →
            </span>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Prefer the classic search?{" "}
            <span
              style={{ color: "#1A5EA8", cursor: "pointer", fontWeight: 600 }}
              onClick={() => navigate("/amyloid/clinicaltrialsearch")}
            >
              Use dropdown filters →
            </span>
          </Typography>
        </Box>
      )}
    </Container>
  );
}